import { IQuizPopupViewModel, QuizVariant, QuizWord } from './i-quiz-popup-view-model';
import { IEnv } from '../../../infrastructure/i-env';
import { RecallStatus } from '../../../dto/user-words';
import type { IPopupContext } from '../../../services/i-popup-service';
import { Language } from '../../../dto/language';

export interface QuizAnswerPayload {
	readonly translatedWordId: string;
	readonly recallStatus: RecallStatus;
	readonly isCorrect: boolean;
}

interface QuizPopupViewModelState {
	isVisible: boolean;
	showFeedbackNotification: boolean;
	lastAnswerCorrect: boolean;
	showAnswerHighlight: boolean;
	selectedAnswer: string;
	isAnswerCorrect: boolean;
	selectedOption: QuizVariant | null;
	showTransliteration: boolean;
	answerOptions: QuizVariant[];
	wordResults: Array<boolean | null>;

	// Quiz state
	stepNumber: number;
	validAnswerCount: number;
	invalidAnswerCount: number;
	isFinished: boolean;
}

/**
 * Framework-agnostic quiz popup view model.
 * Manages quiz state and answer submission logic.
 */
export class QuizPopupViewModel extends IQuizPopupViewModel {
	private state: QuizPopupViewModelState;
	readonly popupId: string = 'quiz';
	private popupContext: IPopupContext<void> | null = null;
	private showPopupTimer: ReturnType<typeof setTimeout> | null = null;
	private hideFeedbackTimer: ReturnType<typeof setTimeout> | null = null;
	public readonly nativeLanguage: Language;
	public readonly learnedLanguage: Language;

	constructor(
			env: IEnv,
			words: QuizWord[],
			nativeLanguage: Language,
			learnedLanguage: Language,
			private readonly onAnswer?: (payload: QuizAnswerPayload) => void | Promise<void>,
	) {
		super(env, words);
		this.nativeLanguage = nativeLanguage;
		this.learnedLanguage = learnedLanguage;
		this.state = env.reactive({
			isVisible: false,
			showFeedbackNotification: false,
			lastAnswerCorrect: false,
			showAnswerHighlight: false,
			selectedAnswer: '',
			isAnswerCorrect: false,
			selectedOption: null,
			showTransliteration: false,
			answerOptions: [],
			wordResults: words.map(() => null),

			// Quiz state
			stepNumber: 0,
			validAnswerCount: 0,
			invalidAnswerCount: 0,
			isFinished: false
		}) as QuizPopupViewModelState;
	}

	get isVisible(): boolean {
		return this.state.isVisible;
	}

	get showFeedbackNotification(): boolean {
		return this.state.showFeedbackNotification;
	}

	get lastAnswerCorrect(): boolean {
		return this.state.lastAnswerCorrect;
	}

	get showAnswerHighlight(): boolean {
		return this.state.showAnswerHighlight;
	}

	get selectedAnswer(): string {
		return this.state.selectedAnswer;
	}

	get isAnswerCorrect(): boolean {
		return this.state.isAnswerCorrect;
	}

	get selectedOption(): QuizVariant | null {
		return this.state.selectedOption;
	}

	get showTransliteration(): boolean {
		return this.state.showTransliteration;
	}

	set showTransliteration(value: boolean) {
		this.state.showTransliteration = value;
	}

	get stepNumber(): number {
		return this.state.stepNumber;
	}

	get validAnswerCount(): number {
		return this.state.validAnswerCount;
	}

	get invalidAnswerCount(): number {
		return this.state.invalidAnswerCount;
	}

	get isFinished(): boolean {
		return this.state.isFinished;
	}

	get totalWords(): number {
		return this.words.length;
	}

	get currentWord(): QuizWord | null {
		return this.state.stepNumber < this.words.length ? this.words[this.state.stepNumber]:null;
	}

	get quizWords(): QuizWord[] {
		return this.words;
	}

	getWordResult(index: number): boolean | null {
		if (index < 0 || index >= this.state.wordResults.length) {
			return null;
		}
		return this.state.wordResults[index];
	}

	get answerOptions(): QuizVariant[] {
		return this.state.answerOptions;
	}

	get scorePercentage(): number {
		const total = this.state.validAnswerCount + this.state.invalidAnswerCount;
		if (total===0) return 0;
		return Math.round((this.state.validAnswerCount / total) * 100);
	}

	init(context: IPopupContext<void>): void {
		this.popupContext = context;
		this.setAnswerOptionsForCurrentWord();
		// Show popup with animation
		if (this.showPopupTimer) clearTimeout(this.showPopupTimer);
		this.showPopupTimer = setTimeout(() => {
			this.state.isVisible = true;
		}, 10);
	}

	selectOption(option: QuizVariant): void {
		if (this.state.showAnswerHighlight) return;
		this.state.selectedOption = option;
	}

	async submitAnswer(recallStatus: RecallStatus): Promise<void> {
		if (this.state.showAnswerHighlight || !this.state.selectedOption) return;

		const answer = this.state.selectedOption.translation;
		const correct = answer===this.currentWord?.validVariant.translation;
		this.state.lastAnswerCorrect = correct;
		this.state.isAnswerCorrect = correct;
		this.state.selectedAnswer = answer;

		// If answer is wrong, always use Again status regardless of what was selected
		const finalRecallStatus = correct ? recallStatus:RecallStatus.Again;

		// Notify host about the answer (best-effort)
		const translatedWordId = this.currentWord?.validVariant.id;
		if (translatedWordId && this.onAnswer) {
			try {
				await this.onAnswer({ translatedWordId, recallStatus: finalRecallStatus, isCorrect: correct });
			} catch (e) {
				this.env.log('Error handling quiz answer', e);
			}
		}

		// Show notification
		this.state.showFeedbackNotification = true;

		// If incorrect, highlight correct and incorrect answers
		if (!correct) {
			this.state.showAnswerHighlight = true;

			// Wait before moving to next question
			await this.delay(900);

			this.state.showAnswerHighlight = false;
		}

		this.setResultForCurrentStep(correct);

		// Move to next question
		await this.answerWord(answer, finalRecallStatus);

		// Reset state
		this.state.selectedAnswer = '';
		this.state.selectedOption = null;

		// Hide notification after a short delay
		if (this.hideFeedbackTimer) clearTimeout(this.hideFeedbackTimer);
		this.hideFeedbackTimer = setTimeout(() => {
			this.state.showFeedbackNotification = false;
		}, 800);
	}

	async handleDontKnow(): Promise<void> {
		if (this.state.showAnswerHighlight) return;

		this.state.lastAnswerCorrect = false;
		this.state.isAnswerCorrect = false;
		this.state.selectedAnswer = '';

		const recallStatus = RecallStatus.Again;

		// Notify host about the answer (best-effort)
		const translatedWordId = this.currentWord?.validVariant.id;
		if (translatedWordId && this.onAnswer) {
			try {
				await this.onAnswer({ translatedWordId, recallStatus, isCorrect: false });
			} catch (e) {
				this.env.log('Error handling quiz answer', e);
			}
		}

		// Show correct answer
		this.state.showAnswerHighlight = true;

		// Wait before moving to next question
		await this.delay(900);

		this.state.showAnswerHighlight = false;

		this.setResultForCurrentStep(false);

		// Move to next question
		await this.answerWord('', recallStatus);

		// Reset state
		this.state.selectedOption = null;
	}

	handleClose(): void {
		if (this.showPopupTimer) clearTimeout(this.showPopupTimer);
		this.showPopupTimer = null;
		if (this.hideFeedbackTimer) clearTimeout(this.hideFeedbackTimer);
		this.hideFeedbackTimer = null;
		this.popupContext?.cancelPopup(undefined);
	}

	getDisplayText(option: QuizVariant): string {
		if (!this.state.showTransliteration) {
			return option.translation;
		}

		// If transliteration is enabled, use it if available
		return option.transliteration || option.translation;
	}

	private async answerWord(answer: string, recallStatus: RecallStatus): Promise<void> {
		const word = this.currentWord;
		if (!word || this.state.isFinished) return;

		const isCorrect = answer===word.validVariant.translation;

		// Update counts based on recall status
		if (recallStatus===RecallStatus.Again) {
			this.state.invalidAnswerCount++;
		} else if (isCorrect) {
			this.state.validAnswerCount++;
		} else {
			this.state.invalidAnswerCount++;
		}

		// Move to next word
		this.state.stepNumber++;
		this.setAnswerOptionsForCurrentWord();

		// Check if quiz is finished
		if (this.state.stepNumber >= this.words.length) {
			this.state.isFinished = true;
		}
	}

	private setAnswerOptionsForCurrentWord(): void {
		const currentWord = this.currentWord;
		if (!currentWord) {
			this.state.answerOptions = [];
			return;
		}

		const invalidVariants = currentWord.invalidVariants;
		if (!invalidVariants || !Array.isArray(invalidVariants)) {
			this.state.answerOptions = [currentWord.validVariant];
			return;
		}

		// Combine valid + invalid, then shuffle ONCE for this step.
		const options: QuizVariant[] = [currentWord.validVariant, ...invalidVariants];
		for (let i = options.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[options[i], options[j]] = [options[j], options[i]];
		}

		this.state.answerOptions = options;
	}

	private delay(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	private setResultForCurrentStep(isCorrect: boolean): void {
		const index = this.state.stepNumber;
		if (index < 0 || index >= this.state.wordResults.length) {
			return;
		}
		this.state.wordResults[index] = isCorrect;
	}
}
